// Sign-in with the MFA challenge step and workspace choice (UX spec 6.16). When the server offers
// Google (B8), "Continue with Google" leads as the path for staff on the allowed domains; the
// password form stays for everyone else. A failed Google callback returns here with ?error=google_*.
import type { Session } from '@bcio-voice/contracts';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { type ApiClient, type DataEnvelope, SsoClient, isApiRequestError } from '../../lib/api.ts';
import { describeError, type ErrorView } from '../../lib/errorCopy.ts';
import { useOptionalRouter } from '../../lib/router.tsx';
import { type LoginStep, type SignOutNotice, loginStepFor, usedAuthenticatorCode } from '../../state/session.ts';
import { APP_NAME, APP_TAGLINE, BRAND_ASSETS } from '../../config/brand.ts';
import { PathIcon } from '../../ui/Icon.tsx';
import { BrandImage } from '../../ui/Wordmark.tsx';
import { NEW_CODE_DETAIL, NEW_CODE_HEADLINE } from '../../identity/IdentityParts.tsx';
import { GOOGLE_MARK, type GoogleProvider, SIGN_IN_PATH, domainList, googleErrorCode, googleErrorCopy, googleHint, readProviders, signInReturnPath } from '../../identity/google.ts';

/** Why this sign-in is shown, when it isn't the workspace itself (for example desktop authorization). */
export interface SignInContext {
  title: string;
  body: string;
}

/** Where this screen is, from the router when one is mounted (tests render without one). */
function useHere(): { path: string; query: string; replace: (to: string) => void } {
  const router = useOptionalRouter();
  if (router) return { path: router.path, query: router.query, replace: to => router.navigate(to, { replace: true }) };
  if (typeof window === 'undefined') return { path: '/', query: '', replace: () => undefined };
  return { path: window.location.pathname, query: window.location.search, replace: to => window.history.replaceState(null, '', to) };
}

export function SignIn({ api, notice, onSignedIn, embedded, context, initialProviders = null }: {
  api: ApiClient;
  notice: SignOutNotice;
  onSignedIn: (session: Session) => void;
  embedded?: boolean;
  context?: SignInContext;
  /** What GET /auth/providers said, when the host already knows (tests, previews). It is re-read on mount. */
  initialProviders?: GoogleProvider | null;
}) {
  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorView | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const here = useHere();
  const sso = useMemo(() => new SsoClient(api), [api]);
  const [google, setGoogle] = useState<GoogleProvider | null>(initialProviders);
  // Read once: the URL is cleaned right after, and the explanation stays until the next attempt.
  const [googleError, setGoogleError] = useState(() => (embedded ? null : googleErrorCode(new URLSearchParams(here.query).get('error'))));

  useEffect(() => {
    if (step === 'mfa') codeRef.current?.focus();
  }, [step]);
  useEffect(() => {
    if (!embedded) emailRef.current?.focus();
  }, [embedded]);
  // Clear secrets from memory when the form goes away.
  useEffect(() => () => { setPassword(''); setCode(''); }, []);
  // Google is offered only when the server says so; an older server, a refusal or no answer hides it.
  useEffect(() => {
    const controller = new AbortController();
    sso.providers(controller.signal).then(
      body => { if (!controller.signal.aborted) setGoogle(readProviders(body)); },
      () => undefined,
    );
    return () => controller.abort();
  }, [sso]);
  // After a failed Google callback: drop ?error from the address and leave /sign-in for the
  // workspace root, so a reload doesn't repeat the message and a password sign-in lands on a page.
  useEffect(() => {
    if (embedded) return;
    const onLanding = here.path === SIGN_IN_PATH || here.path.startsWith(`${SIGN_IN_PATH}/`);
    if (!onLanding && !new URLSearchParams(here.query).has('error')) return;
    here.replace(signInReturnPath(here.path, here.query));
    // Only the address this screen opened with, once.
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (step === 'mfa' && !/^[0-9]{6}$/.test(code)) {
      setError({ title: 'Enter the 6-digit code from your authenticator app.', detail: '', code: 'mfa_format', requestId: null, retryable: true });
      return;
    }
    setBusy(true);
    setError(null);
    setGoogleError(null);
    try {
      const env = await api.send<DataEnvelope<Session>>('POST', '/auth/login', { email: email.trim(), password, ...(step === 'mfa' ? { mfa_code: code } : {}) });
      setPassword('');
      setCode('');
      onSignedIn(env.data);
    } catch (err) {
      const next = isApiRequestError(err) ? loginStepFor(err.code, step) : step;
      if (next === 'mfa' && step !== 'mfa') {
        setStep('mfa');
        setError(null);
      } else {
        setStep(next);
        setError(describeError(err, "Couldn't sign in"));
        if (step === 'mfa') setCode('');
      }
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setStep('credentials');
    setCode('');
    setError(null);
  };

  const offerGoogle = google?.enabled === true && (step === 'credentials' || step === 'limited');
  const googleLeads = offerGoogle && !embedded;

  const form = (
    <form className="auth-form" onSubmit={submit} noValidate aria-describedby={error ? 'signin-error' : undefined}>
      {notice === 'expired' && step === 'credentials' ? (
        <p className="notice notice-warn" role="status">Your session expired. Sign in again to continue.</p>
      ) : null}
      {notice === 'signed_out' && step === 'credentials' ? <p className="notice" role="status">You signed out.</p> : null}
      {notice === 'policy' && step === 'credentials' ? (
        <p className="notice notice-warn" role="status">Your access changed. Sign in again, including your verification code, to continue.</p>
      ) : null}
      {step === 'credentials' && notice ? <IdentityNotice notice={notice} /> : null}
      {googleError && step === 'credentials' ? <GoogleErrorNotice code={googleError} domains={google ? google.allowedDomains : null} /> : null}
      {googleLeads && google ? (
        <>
          <div style={GOOGLE_STACK}>
            {/* A plain link: the API sends the browser to Google and back. No OAuth runs in this page. */}
            <a className="btn btn-primary btn-block" href={sso.googleStartUrl(signInReturnPath(here.path, here.query))} aria-describedby="signin-google-hint" style={TALL}>
              <PathIcon paths={GOOGLE_MARK} />
              <span>Continue with Google</span>
            </a>
            <p className="field-hint" id="signin-google-hint">{googleHint(google)}</p>
          </div>
          <p style={OR_RULE}>
            <span style={OR_LINE} aria-hidden="true" />
            <span>Or use your email and password</span>
            <span style={OR_LINE} aria-hidden="true" />
          </p>
        </>
      ) : null}
      {offerGoogle && embedded ? (
        <p className="notice" role="note">Signing in with Google leaves this page, which would end your call. If you only sign in with Google, do it after the call.</p>
      ) : null}
      {step === 'credentials' || step === 'limited' ? (
        <>
          <div className="field">
            <label htmlFor="signin-email">Work email</label>
            <input ref={emailRef} id="signin-email" name="email" type="email" autoComplete="username" required value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="signin-password">Password</label>
            <input id="signin-password" name="password" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} />
          </div>
        </>
      ) : null}
      {step === 'mfa' ? (
        <div className="field">
          <label htmlFor="signin-code">Verification code</label>
          <p className="field-hint" id="signin-code-hint">
            {usedAuthenticatorCode(notice)
              ? `Enter a new 6-digit code from your authenticator app for ${email.trim()}. The code you entered a moment ago can’t be used again, so wait for the app to show the next one.`
              : `Enter the 6-digit code from your authenticator app for ${email.trim()}.`}
          </p>
          <input
            ref={codeRef}
            id="signin-code"
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            aria-describedby="signin-code-hint"
            aria-invalid={error ? true : undefined}
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        </div>
      ) : null}
      {step === 'enrollment_required' ? (
        <p className="notice notice-warn">Your role requires two-step verification, and it isn’t set up for your account yet. Ask an administrator to set it up, then sign in again.</p>
      ) : null}
      {error ? (
        <div id="signin-error" className="form-error" role="alert">
          <strong>{error.title}</strong>
          {error.detail ? <span> {error.detail}</span> : null}
          {error.requestId ? <span className="ref"> Reference <span className="mono">{error.requestId}</span></span> : null}
        </div>
      ) : null}
      <div className="form-actions">
        {step === 'mfa' || step === 'enrollment_required' ? (
          <button type="button" className="btn" onClick={restart}>Use a different account</button>
        ) : null}
        {step !== 'enrollment_required' ? (
          // With Google leading, the password button steps back to an outline at the same height.
          <button type="submit" className={googleLeads ? 'btn' : 'btn btn-primary'} style={googleLeads ? SECONDARY_SUBMIT : undefined} disabled={busy} aria-busy={busy || undefined}>
            {busy ? 'Signing in…' : step === 'mfa' ? 'Verify and sign in' : 'Sign in'}
          </button>
        ) : null}
      </div>
    </form>
  );

  if (embedded) return form;
  const openDomains = google?.enabled && google.allowedDomains.length ? google.allowedDomains : null;
  return (
    <main id="main" className="auth-page">
      <div className="auth-card">
        <p className="auth-brand"><BrandImage asset={BRAND_ASSETS.lockup} height={32} /></p>
        <h1 className="auth-title">{step === 'mfa' ? 'Two-step verification' : context?.title ?? 'Sign in'}</h1>
        {context ? <p className="field-hint">{context.body}</p> : <p className="auth-tagline">{APP_TAGLINE}</p>}
        {form}
        {step === 'credentials' || step === 'limited' ? (
          // A full page load on purpose: this screen never holds a call or workspace state.
          <p className="auth-foot"><a href="/recover">Forgot your password?</a></p>
        ) : null}
        <p className="auth-foot">
          {openDomains
            ? `Anyone with a verified ${domainList(openDomains)} Google account can sign in. Everyone else needs an invitation.`
            : 'Access is by invitation. There is no public sign-up.'}
        </p>
      </div>
    </main>
  );
}

// Layout for the Google block, from the Paper and Line tokens. Inline because this screen is
// imported directly by tests, which can't load a stylesheet; app.css belongs to the shell lane.
const GOOGLE_STACK = { display: 'grid', gap: 'var(--space-2)' } as const;
const TALL = { minHeight: 'var(--target-critical)' } as const;
const SECONDARY_SUBMIT = { minHeight: 'var(--target-critical)', minWidth: '140px' } as const;
const OR_RULE = { display: 'flex', alignItems: 'center', gap: 'var(--space-3)', margin: 0, color: 'var(--ink-2)', fontSize: 'var(--text-meta)', lineHeight: 'var(--leading-meta)' } as const;
const OR_LINE = { flex: '1 1 0', height: '1px', background: 'var(--rule-strong)' } as const;

/** Why the Google sign-in came back here, and what to do next. */
function GoogleErrorNotice({ code, domains }: { code: NonNullable<ReturnType<typeof googleErrorCode>>; domains: readonly string[] | null }) {
  const view = googleErrorCopy(code, domains);
  return (
    <div className="form-error" role="alert" data-google-error={view.code}>
      <strong>{view.title}</strong> <span>{view.detail}</span>
    </div>
  );
}

/** After a B6 identity workflow ended the session on purpose. */
function IdentityNotice({ notice }: { notice: NonNullable<SignOutNotice> }) {
  const text =
    notice === 'password_reset' ? 'Your password was changed and you were signed out everywhere. Sign in with your new password.'
      : notice === 'password_reset_verified' ? `Your password was changed and you were signed out everywhere. Use your new password. ${NEW_CODE_DETAIL}`
        : notice === 'mfa_enrolled' ? `Two-step verification is on, and you were signed out everywhere. ${NEW_CODE_DETAIL}`
          : notice === 'invite_joined' ? 'You joined the workspace. Sign in to start.'
            : notice === 'invite_joined_verified' ? `You joined the workspace. ${NEW_CODE_DETAIL}`
              : notice === 'fresh_sign_in' ? 'Sign in again to continue.'
                : null;
  if (!text) return null;
  return (
    <p className="notice notice-info" role="status">
      {usedAuthenticatorCode(notice) ? <><strong>{NEW_CODE_HEADLINE}</strong> </> : null}
      {text}
    </p>
  );
}

export function TenantChooser({ api, session, onChosen, onSignOut, context }: { api: ApiClient; session: Session; onChosen: (s: Session) => void; onSignOut: () => void; context?: string }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<ErrorView | null>(null);
  const choose = async (tenantId: string) => {
    setBusy(tenantId);
    setError(null);
    try {
      const env = await api.send<DataEnvelope<Session>>('POST', '/auth/tenant', { tenant_id: tenantId });
      onChosen(env.data);
    } catch (err) {
      setError(describeError(err, "Couldn't open that workspace"));
    } finally {
      setBusy(null);
    }
  };
  return (
    <main id="main" className="auth-page">
      <div className="auth-card">
        <p className="auth-brand"><BrandImage asset={BRAND_ASSETS.lockup} height={32} /></p>
        <h1 className="auth-title">Choose a workspace</h1>
        {context ? <p className="field-hint">{context}</p> : null}
        {session.memberships.length === 0 ? (
          <p className="notice notice-warn">Your account has no active workspace. Ask an administrator to add you.</p>
        ) : (
          <ul className="choice-list">
            {session.memberships.map(m => (
              <li key={m.tenant_id}>
                <button type="button" className="choice" onClick={() => choose(m.tenant_id)} disabled={busy !== null} aria-busy={busy === m.tenant_id || undefined}>
                  <span className="choice-title">{m.name}</span>
                  <span className="choice-meta">{m.role_ids.join(', ') || 'No role'}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {error ? <p className="form-error" role="alert">{error.title}{error.requestId ? ` Reference ${error.requestId}.` : ''}</p> : null}
        <button type="button" className="btn btn-quiet" onClick={onSignOut}>Sign out</button>
      </div>
    </main>
  );
}
